import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="auto-submit"
//
// Auto-submits the form after input/change events. Because filter forms use a
// GET request that triggers a full Turbo Drive navigation (replacing <body>),
// the focused search field would normally lose focus and the caret position on
// every keystroke. We persist the active field + caret in sessionStorage before
// submitting and restore it once the new page connects, so typing a name into
// the search box is not interrupted.
const STORAGE_KEY = "auto-submit:focus"

export default class extends Controller {
  connect() {
    this.timeout = null
    this.restoreFocus()
  }

  save(event) {
    clearTimeout(this.timeout)

    const field = event?.target
    this.timeout = setTimeout(() => {
      this.persistFocus(field)
      this.element.requestSubmit()
    }, this.delay(field))
  }

  // Longer debounce for free-text search so we don't navigate on every keystroke.
  delay(field) {
    if (field && field.type !== "text" && field.type !== "search") return 150
    return 500
  }

  persistFocus(field) {
    if (!field || !field.id) return

    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          id: field.id,
          start: field.selectionStart,
          end: field.selectionEnd
        })
      )
    } catch (_e) {
      // sessionStorage may be unavailable (private mode); focus restore is best-effort.
    }
  }

  restoreFocus() {
    let stored
    try {
      stored = sessionStorage.getItem(STORAGE_KEY)
      sessionStorage.removeItem(STORAGE_KEY)
    } catch (_e) {
      return
    }
    if (!stored) return

    const { id, start, end } = JSON.parse(stored)
    const field = id && this.element.querySelector(`#${CSS.escape(id)}`)
    if (!field) return

    field.focus()
    if (typeof start === "number" && field.setSelectionRange) {
      try {
        field.setSelectionRange(start, end)
      } catch (_e) {
        // Some input types don't support setSelectionRange; focus alone is fine.
      }
    }
  }

  disconnect() {
    clearTimeout(this.timeout)
  }
}
